import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AmbassadorShell, { card, eyebrow, ghostBtn, gold, h1Style, ink, line, muted, primaryBtn, Reveal } from "./_AmbassadorShell";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { getAmbassadorDashboard, type AmbassadorDashboard } from "@/lib/ambassadorApi";

export default function AmbassadorDashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState<AmbassadorDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    getAmbassadorDashboard()
      .then((d) => { if (!cancelled) { setData(d); setErrorMsg(null); } })
      .catch((e: unknown) => {
        if (!cancelled) setErrorMsg(e instanceof Error ? e.message : "Couldn't load dashboard");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  const amb = data?.ambassador ?? null;
  const stats = data?.stats ?? null;
  const rewards = data?.rewards ?? [];
  const shareLink = useMemo(() => {
    if (data?.referral_url) return data.referral_url;
    const code = data?.referral_code || amb?.code;
    return code ? `${window.location.origin}/r/${code}` : "";
  }, [data, amb]);
  const referralCode = data?.referral_code || amb?.code || "";
  const isActive = amb?.status === "active";
  const tier = useMemo(() => tierFor(stats?.referrals_active || 0), [stats]);
  const nextPayout = useMemo(() => {
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const days = Math.max(1, Math.round((next.getTime() - now.getTime()) / 86400000));
    return { label: next.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }), days };
  }, []);
  const atTopTier = (stats?.referrals_active ?? 0) >= 25;

  if (loading) return <AmbassadorShell><p style={{ color: muted }}>Loading your dashboard…</p></AmbassadorShell>;

  if (errorMsg && !amb) {
    return (
      <AmbassadorShell>
        <Reveal>
          <p style={eyebrow}>Couldn't load dashboard</p>
          <h1 style={h1Style}>One sec.</h1>
          <p style={{ color: muted, marginBottom: 16, fontSize: 17 }}>{errorMsg}</p>
          <button style={primaryBtn} onClick={() => window.location.reload()}>Try again</button>
        </Reveal>
      </AmbassadorShell>
    );
  }

  if (!amb) {
    return (
      <AmbassadorShell>
        <Reveal>
          <p style={eyebrow}>
            <span aria-hidden style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: "#C4823A", boxShadow: "0 0 8px #C4823A", marginRight: 8, verticalAlign: "middle" }} />
            Not an Insider yet
          </p>
          <h1 style={h1Style}>We haven't met yet.</h1>
          <p style={{ color: muted, marginBottom: 24, fontSize: 18 }}>
            Two minutes to apply. I'll read it personally and get back to you within 48 hours.
          </p>
          <Link to="/ambassadors/apply" style={primaryBtn}>Apply now →</Link>
        </Reveal>
      </AmbassadorShell>
    );
  }

  return (
    <AmbassadorShell wide>
      <Reveal>
        <p style={eyebrow}>
          <span aria-hidden style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: isActive ? "#2a7a4a" : "#C4823A", boxShadow: `0 0 8px ${isActive ? "#2a7a4a" : "#C4823A"}`, marginRight: 8, verticalAlign: "middle" }} />
          Welcome back · status: {amb.status}
        </p>
        <h1 style={h1Style}>Hey {amb.display_name?.split(" ")[0] ?? "there"}.</h1>
      </Reveal>

      {amb.status === "pending" && (
        <div style={{ ...card, background: "#FFF8E5", borderColor: "#E8C56A", marginBottom: 32 }}>
          <strong>I'm reading your application.</strong> <span style={{ color: muted }}>You'll hear from me within 48 hours. Until then your share link is paused — sit tight.</span>
        </div>
      )}

      <section style={{ ...card, marginBottom: 24, opacity: isActive ? 1 : 0.6 }}>
        <div style={eyebrow}>Your share link · this is the one that pays</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
          <code style={{ flex: 1, padding: "12px 14px", background: "#FBF8F1", borderRadius: 8, fontSize: 14, minWidth: 260, fontFamily: "ui-monospace, monospace" }}>{shareLink || "—"}</code>
          <button style={ghostBtn} disabled={!isActive || !shareLink} onClick={() => { if (shareLink) { navigator.clipboard.writeText(shareLink); toast({ title: "Copied" }); } }}>Copy link</button>
          <button style={ghostBtn} disabled={!isActive || !referralCode} onClick={() => { if (referralCode) { navigator.clipboard.writeText(referralCode); toast({ title: "Code copied" }); } }}>Copy code: {referralCode || "—"}</button>
        </div>
        <p style={{ color: muted, fontSize: 13, marginTop: 12 }}>
          {isActive
            ? <>Your link tags every signup back to you. No codes for buyers to remember, no discount weirdness — they pay full price. <em>You earn on what they pay.</em></>
            : <>Goes live the moment you're approved. Until then, signups won't attribute back to you.</>}
        </p>
      </section>

      <section style={{ ...card, background: ink, color: "#F8F4EC", borderColor: ink, marginBottom: 16 }}>
        <div style={{ ...eyebrow, color: "rgba(248,244,236,0.6)", marginBottom: 8 }}>Next payout · {nextPayout.label} · ~{nextPayout.days} days</div>
        <div style={{ fontSize: 56, fontFamily: "'Instrument Serif', serif", fontStyle: "italic", lineHeight: 1 }}>
          ${((stats?.approved_cents ?? 0) / 100).toFixed(2)}
        </div>
        <div style={{ marginTop: 6, color: "rgba(248,244,236,0.7)", fontSize: 13 }}>Approved · cleared the refund hold, ships on the 1st once over the payout minimum.</div>
      </section>

      <div style={statGrid}>
        <Stat label="Active referrals" value={stats?.referrals_active ?? 0} />
        <Stat label="Pending payout" value={`$${((stats?.pending_cents ?? 0) / 100).toFixed(2)}`} />
        <Stat label="Lifetime paid" value={`$${((stats?.paid_cents ?? 0) / 100).toFixed(2)}`} />
        <Stat label="Lifetime referrals" value={stats?.referrals_total ?? 0} />
      </div>
      <p style={{ color: muted, fontSize: 12, marginTop: 10, textAlign: "right" }}>
        Link clicks: <strong style={{ color: ink, fontWeight: 600 }}>{stats?.clicks_total ?? 0}</strong>
      </p>

      {rewards.length > 0 && (
        <section style={{ ...card, marginTop: 24 }}>
          <div style={eyebrow}>Rewards · what's been credited</div>
          <div style={{ overflowX: "auto", marginTop: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ textAlign: "left", color: muted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  <th style={{ padding: "8px 12px 8px 0" }}>Label</th>
                  <th style={{ padding: "8px 12px" }}>Period</th>
                  <th style={{ padding: "8px 12px" }}>Amount</th>
                  <th style={{ padding: "8px 12px" }}>Status</th>
                  <th style={{ padding: "8px 0 8px 12px" }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {rewards.map((r, idx) => (
                  <tr key={r.id ?? idx} style={{ borderTop: `1px solid ${line}` }}>
                    <td style={{ padding: "10px 12px 10px 0" }}>{r.label ?? "—"}</td>
                    <td style={{ padding: "10px 12px", color: muted }}>{r.period_label ?? "—"}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 600 }}>${((r.amount_cents ?? 0) / 100).toFixed(2)}</td>
                    <td style={{ padding: "10px 12px", textTransform: "capitalize" }}>{r.status ?? "—"}</td>
                    <td style={{ padding: "10px 0 10px 12px", color: muted }}>
                      {r.occurred_at ? new Date(r.occurred_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section style={{ ...card, marginTop: 24 }}>
        <div style={eyebrow}>Circle tier</div>
        <div style={{ display: "flex", gap: 16, alignItems: "baseline", marginTop: 6 }}>
          <div style={{ fontSize: 28, fontFamily: "'Instrument Serif', serif", fontStyle: "italic" }}>{tier.label}</div>
          <div style={{ color: muted, fontSize: 14 }}>{tier.description}</div>
        </div>
        {!atTopTier && (
          <div style={{ marginTop: 12, height: 8, background: "#F0E9DA", borderRadius: 999, overflow: "hidden" }}>
            <div style={{ width: `${Math.min(100, ((stats?.referrals_active ?? 0) / tier.nextAt) * 100)}%`, height: "100%", background: gold }} />
          </div>
        )}
        <p style={{ color: muted, fontSize: 13, marginTop: 8 }}>
          {atTopTier
            ? "Top tier — your Circle meets weekly with the founder."
            : `${tier.nextAt - (stats?.referrals_active ?? 0)} more active referrals to unlock ${tier.nextLabel}.`}
        </p>
        <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Link to="/ambassadors/circle" style={primaryBtn}>Open my Circle →</Link>
          <Link to="/ambassadors/terms" style={ghostBtn}>Program terms</Link>
        </div>
      </section>
    </AmbassadorShell>
  );
}

function tierFor(count: number) {
  if (count >= 25) return { label: "Tier 2 · Weekly founder drop-in", description: "Founder drops into your Circle every week for a full hour. You host the room.", nextAt: 25, nextLabel: "" };
  if (count >= 5) return { label: "Tier 1 · Monthly founder drop-in", description: "Founder drops into your Circle once a month for 30 minutes. You host the room.", nextAt: 25, nextLabel: "Tier 2 (weekly founder drop-in)" };
  return { label: "Building", description: "Hit 5 active referrals to unlock the first monthly founder drop-in to your Circle. You host the room — the founder just shows up.", nextAt: 5, nextLabel: "Tier 1 (monthly founder drop-in)" };
}

function Stat({ label, value, accent }: { label: string; value: string | number; accent?: boolean }) {
  return (
    <div style={{ ...card, background: accent ? ink : "#fff", color: accent ? "#F8F4EC" : ink, borderColor: accent ? ink : line }}>
      <div style={{ ...eyebrow, color: accent ? "rgba(248,244,236,0.6)" : muted, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontFamily: "'Instrument Serif', serif", fontStyle: "italic", lineHeight: 1 }}>{value}</div>
    </div>
  );
}

const statGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
};